/**
 * Narrative Loader
 *
 * Auto-discovers narrative JSON files in the /src/narratives folder.
 * Each JSON file should have:
 * - metadata.report_id: Unique identifier used in ?report= URL param
 * - narrativeDataValues: Object containing placeholder values
 * - sections: Array of narrative sections
 *
 * To add a new narrative:
 * 1. Create a JSON file in src/narratives/ (e.g., my_report.json)
 * 2. Include metadata.report_id, narrativeDataValues, and sections
 * 3. The loader will automatically pick it up
 *
 * Runtime validation:
 * - All narratives are validated against the Zod schema on load
 * - Invalid narratives are skipped with clear error messages
 * - Run `npm run validate:narratives` to validate all files
 */

import type { NewNarrativeData, DataValues } from "../types/narrative";
import {
  validateNarrativeLenient,
  formatValidationErrors,
} from "@juiceforward/narrative-schema/validation";

// Extended narrative type that includes data values
export interface NarrativeWithDataValues extends NewNarrativeData {
  narrativeDataValues: DataValues;
}

export interface NarrativeEntry {
  narrative: NewNarrativeData;
  dataValues: DataValues;
}

// Use Vite's import.meta.glob to auto-discover all JSON files in the narratives folder
// eager: true loads them synchronously at build time
// Recursive pattern (./**/*.json) allows project-based folder organization
// Exclude manifest.json files (used by DevNavigator, not narratives)
const narrativeModules = import.meta.glob<NarrativeWithDataValues>(
  ["./**/*.json", "!./**/manifest.json", "!./pipeline/**/_*/**"],
  { eager: true, import: "default" },
);

/**
 * Build the registry from auto-discovered files
 * Maps report_id -> { narrative, dataValues }
 */
function buildNarrativeRegistry(): Record<string, NarrativeEntry> {
  const registry: Record<string, NarrativeEntry> = {};

  for (const [path, data] of Object.entries(narrativeModules)) {
    // Validate against schema (lenient: strips unrecognized config instead of rejecting)
    const result = validateNarrativeLenient(data);

    if (result.warnings.length > 0 && import.meta.env.DEV) {
      console.warn(`⚠️  Sanitized unrecognized config in ${path}:`);
      result.warnings.forEach((w) => console.warn(`  • ${w}`));
    }

    if (!result.success) {
      // Only log validation errors in development mode
      if (import.meta.env.DEV) {
        console.warn(`⚠️  Schema validation failed for ${path}:`);
        console.warn(formatValidationErrors(result.error));
      }
      continue; // Skip invalid narratives
    }

    const validatedData = result.data;
    const reportId = validatedData.metadata?.report_id;

    if (!reportId) {
      console.warn(
        `Narrative at ${path} is missing metadata.report_id, skipping`,
      );
      continue;
    }

    // Extract narrativeDataValues and create clean narrative object
    const { narrativeDataValues, ...narrativeData } = validatedData;

    registry[reportId] = {
      narrative: narrativeData as NewNarrativeData,
      dataValues: narrativeDataValues as unknown as DataValues,
    };
  }

  return registry;
}

// Build registry once at module load time
const narrativeRegistry = buildNarrativeRegistry();

/**
 * Get narrative entry by report_id.
 * Returns null if not found - no defaults, maintains privacy.
 */
export function getNarrativeByReportId(
  reportId: string | null,
): NarrativeEntry | null {
  if (!reportId) {
    return null;
  }
  return narrativeRegistry[reportId] || null;
}

/**
 * Check if a specific report ID exists
 */
export function hasNarrative(reportId: string): boolean {
  return reportId in narrativeRegistry;
}

/**
 * Get all available report IDs (for admin/debug purposes only)
 */
export function getAvailableReportIds(): string[] {
  return Object.keys(narrativeRegistry);
}

// ---------------------------------------------------------------------------
// Pipeline manifest discovery (for DevNavigator side panel)
// ---------------------------------------------------------------------------

export interface PipelineManifestEntry {
  customerId: string;
  customerName: string;
  file: string;
  status: string;
  flagged: boolean;
  flagReason?: string;
  flagReasons?: string[];
  excludeReason?: string;
  defaultsUsed?: string[];
  sectionCount: number;
  groupCount: number;
  sectionGroups?: Record<string, string[]>;
  metadata?: {
    report_id?: string;
    admin_emails?: string;
    [key: string]: unknown;
  };
}

export interface PipelineManifest {
  runId: string;
  generatedAt?: string;
  entries: PipelineManifestEntry[];
}

export interface PipelineManifestInfo {
  project: string;
  runId: string;
  manifest: PipelineManifest;
  /**
   * Stable, unique key for the run switcher. Defaults to `${project}/${runId}`.
   * Dispatch-backed runs supply their numeric id: run names are not unique
   * ("Standalone Narratives" exists once per workspace) and a fetched
   * manifest's `runId` is the server's run identifier, not the run name, so
   * neither is safe to select on.
   */
  key?: string;
  /** Human label for the run switcher. Defaults to `${project}/${runId}`. */
  label?: string;
}

/** The switcher key for a manifest info, honoring an explicit `key`. */
export function manifestInfoKey(info: PipelineManifestInfo): string {
  return info.key ?? `${info.project}/${info.runId}`;
}

/** The switcher label for a manifest info, honoring an explicit `label`. */
export function manifestInfoLabel(info: PipelineManifestInfo): string {
  return info.label ?? `${info.project}/${info.runId}`;
}

const manifestModules = import.meta.glob<PipelineManifest>(
  "./pipeline/**/manifest.json",
  { eager: true, import: "default" },
);

/**
 * Return all discovered pipeline manifests with their project name extracted from the path.
 */
export function getAllManifests(): PipelineManifestInfo[] {
  return Object.entries(manifestModules).map(([path, manifest]) => {
    // path looks like "./pipeline/{project}/{runId}/manifest.json"
    const parts = path.split("/");
    const project = parts[2] || "unknown";
    return { project, runId: manifest.runId, manifest };
  });
}

/**
 * Get the pipeline manifest that contains a given report_id.
 * Returns null if the report is not from a pipeline run.
 */
export function getManifestForReportId(
  reportId: string,
): PipelineManifest | null {
  const result = getManifestInfoForReportId(reportId);
  return result?.manifest ?? null;
}

/**
 * Get the manifest info (including project/runId path) for a given report_id.
 * Returns null if the report is not from a pipeline run.
 */
export function getManifestInfoForReportId(
  reportId: string,
):
  | (PipelineManifestInfo & { entry: PipelineManifestEntry; globPath: string })
  | null {
  // Iterate in reverse so the last-discovered manifest wins, matching
  // buildNarrativeRegistry()'s last-write-wins resolution for duplicate report_ids.
  const entries = Object.entries(manifestModules);
  for (let i = entries.length - 1; i >= 0; i--) {
    const [path, manifest] = entries[i];
    const entry = manifest.entries.find(
      (e) => e.metadata?.report_id === reportId,
    );
    if (entry) {
      const parts = path.split("/");
      const project = parts[2] || "unknown";
      return {
        project,
        runId: manifest.runId,
        manifest,
        entry,
        globPath: path,
      };
    }
  }
  return null;
}

/**
 * Fetch a pipeline narrative from static files in public/narratives/pipeline/.
 * Used for large runs (prefixed with _) where JSONs are served statically
 * instead of being bundled into the JS module graph.
 *
 * The URL is derived from the manifest's glob path:
 *   manifest at "./pipeline/{project}/{runId}/manifest.json"
 *   → static file at "/narratives/pipeline/{project}/{runId}/{file}"
 */
export async function fetchStaticNarrative(
  reportId: string,
): Promise<NarrativeEntry | null> {
  const info = getManifestInfoForReportId(reportId);
  if (
    !info ||
    info.entry.file === "(excluded)" ||
    info.entry.file === "(error)"
  ) {
    return null;
  }

  // Derive the base directory from the manifest's glob path
  // e.g. "./pipeline/ts_eosy_2026/_prod/manifest.json" → "pipeline/ts_eosy_2026/_prod"
  const manifestDir = info.globPath
    .replace(/^\.\//, "")
    .replace(/\/manifest\.json$/, "");
  const url = `/narratives/${manifestDir}/${info.entry.file}`;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;

    const data: NarrativeWithDataValues = await response.json();
    const result = validateNarrativeLenient(data);

    if (result.warnings.length > 0 && import.meta.env.DEV) {
      console.warn(
        `⚠️  Sanitized unrecognized config in static narrative at ${url}:`,
      );
      result.warnings.forEach((w) => console.warn(`  • ${w}`));
    }

    if (!result.success) {
      if (import.meta.env.DEV) {
        console.warn(
          `⚠️  Schema validation failed for static narrative at ${url}:`,
        );
        console.warn(formatValidationErrors(result.error));
      }
      return null;
    }

    const { narrativeDataValues, ...narrativeData } = result.data;
    return {
      narrative: narrativeData as NewNarrativeData,
      dataValues: narrativeDataValues as unknown as DataValues,
    };
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn(`⚠️ fetchStaticNarrative: error fetching ${url}:`, err);
    }
    return null;
  }
}

// ---------------------------------------------------------------------------

/**
 * Allowed remote URL prefix for narrative fetching.
 * Only URLs starting with this prefix are permitted — all others are rejected.
 * This prevents content injection via arbitrary `?report=` URLs.
 */
const ALLOWED_REMOTE_PREFIX = "https://raw.githubusercontent.com/juiceinc/";

/**
 * Fetch a narrative from a remote URL (for privately-hosted narratives not bundled with the app).
 *
 * Currently restricted to raw.githubusercontent.com/juiceinc/ — auth is embedded
 * in the URL (GitHub's temporary token query parameter), so no Bearer header is needed.
 *
 * Usage: ?report=https://raw.githubusercontent.com/juiceinc/repo/sha/path.json?token=GHSAT...
 *
 * The remote JSON must follow the same narrative schema as bundled narratives.
 */
export async function fetchRemoteNarrative(
  url: string,
): Promise<NarrativeEntry | null> {
  try {
    // Validate against allowlist to prevent content injection.
    // Parse both the candidate URL and the allowed prefix explicitly so that
    // spoofed or malformed URLs (e.g. "https://raw.githubusercontent.com.evil.com/juiceincs/…")
    // that pass a naïve startsWith check are rejected.
    const _allowedBase = new URL(ALLOWED_REMOTE_PREFIX);
    const allowedProtocol = _allowedBase.protocol; // "https:"
    const allowedHostname = _allowedBase.hostname; // "raw.githubusercontent.com"
    const allowedFirstSegment = _allowedBase.pathname.split("/")[1]; // "juiceincs"

    let parsed: URL;
    try {
      parsed = new URL(url);
    } catch {
      if (import.meta.env.DEV) {
        console.warn(
          `⚠️ Remote URL is invalid and could not be parsed: ${url}`,
        );
      }
      return null;
    }

    const urlFirstSegment = parsed.pathname.split("/")[1];
    if (
      parsed.protocol !== allowedProtocol ||
      parsed.hostname !== allowedHostname ||
      urlFirstSegment !== allowedFirstSegment
    ) {
      if (import.meta.env.DEV) {
        console.warn(
          `⚠️ Remote URL not allowed. Must be under: ${ALLOWED_REMOTE_PREFIX}`,
        );
      }
      return null;
    }

    const headers: HeadersInit = { Accept: "application/json" };

    const response = await fetch(url, { headers });
    if (!response.ok) {
      return null;
    }

    const data: NarrativeWithDataValues = await response.json();
    const result = validateNarrativeLenient(data);

    if (result.warnings.length > 0 && import.meta.env.DEV) {
      console.warn(
        `⚠️  Sanitized unrecognized config in remote narrative at ${url}:`,
      );
      result.warnings.forEach((w) => console.warn(`  • ${w}`));
    }

    if (!result.success) {
      if (import.meta.env.DEV) {
        console.warn(
          `⚠️  Schema validation failed for remote narrative at ${url}:`,
        );
        console.warn(formatValidationErrors(result.error));
      }
      return null;
    }

    const { narrativeDataValues, ...narrativeData } = result.data;
    return {
      narrative: narrativeData as NewNarrativeData,
      dataValues: narrativeDataValues as unknown as DataValues,
    };
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn(
        `⚠️ fetchRemoteNarrative: unexpected error for ${url}:`,
        err,
      );
    }
    return null;
  }
}
